import { useCallback } from 'react';
import { formatCategoryForDisplay } from '@terra/ui-design-system';
import { useProxyStore, type HttpRequest } from '../../store/proxy-store';
import type { OpenMainContentTabInput } from '../../store/layoutStore';
import type { SignalDto } from '@terra/shared-contracts-signals';
import type { EndpointSearchData } from '../useGlobalSearch';

/**
 * Navigation handlers consumed by `<GlobalSearchDialog>`.
 * Each handler opens or navigates to a specific view via the active tab.
 */
export function useSearchDialogNavigation(
  navigateInActiveTab: (tab: OpenMainContentTabInput) => void,
) {
  const handleNavigateToEndpoint = useCallback(
    (endpoint: EndpointSearchData) => {
      const path = endpoint.path || endpoint.url || '';
      const title = endpoint.request?.method
        ? `${endpoint.request.method} ${path}`
        : path;
      navigateInActiveTab({
        id: `endpoint-${endpoint.id}`,
        title,
        type: 'endpoint',
        data: { endpointId: endpoint.id },
      });
    },
    [navigateInActiveTab],
  );

  const handleNavigateToSignal = useCallback(
    (signal: SignalDto) => {
      navigateInActiveTab({
        id: `signal-${signal.id}`,
        title:
          signal.description ||
          formatCategoryForDisplay(signal.category) ||
          'Security Signal',
        type: 'signal',
        data: { signalId: signal.id },
      });
    },
    [navigateInActiveTab],
  );

  const handleNavigateToRequest = useCallback(
    (request: HttpRequest) => {
      useProxyStore.getState().setSelectedRequest(request);
      navigateInActiveTab({
        id: 'requests',
        title: 'HTTP History',
        type: 'requests',
      });
    },
    [navigateInActiveTab],
  );

  const handleOpenSettings = useCallback(() => {
    navigateInActiveTab({
      id: 'settings',
      title: 'Target Settings',
      type: 'settings',
    });
  }, [navigateInActiveTab]);

  const handleOpenHistory = useCallback(() => {
    navigateInActiveTab({
      id: 'requests',
      title: 'HTTP History',
      type: 'requests',
    });
  }, [navigateInActiveTab]);

  const handleOpenIntercept = useCallback(() => {
    navigateInActiveTab({
      id: 'intercept',
      title: 'Intercept',
      type: 'intercept',
    });
  }, [navigateInActiveTab]);

  const handleOpenReplay = useCallback(() => {
    navigateInActiveTab({
      id: 'replay',
      title: 'Replay',
      type: 'replay',
    });
  }, [navigateInActiveTab]);

  return {
    handleNavigateToEndpoint,
    handleNavigateToSignal,
    handleNavigateToRequest,
    handleOpenSettings,
    handleOpenHistory,
    handleOpenIntercept,
    handleOpenReplay,
  };
}
